import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, Loader2, Eye, EyeOff, ExternalLink, X, Plus, Link2, Copy, Check, QrCode, Download } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

/**
 * /admin/content/new  → create
 * /admin/content/:id  → edit
 *
 * Simple split editor: metadata + markdown textarea on the left, live rendered
 * preview on the right. Adequate for admin publishing without a heavy WYSIWYG.
 */
const EMPTY = {
  title: "", slug: "", excerpt: "", body_md: "", cover_url: "",
  sector_tags: [], author: "Connexis Insights",
  seo_title: "", seo_description: "",
  status: "draft",
};

export default function ContentEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/content/articles/${id}`);
        setForm({
          title:           res.data.title || "",
          slug:            res.data.slug || "",
          excerpt:         res.data.excerpt || "",
          body_md:         res.data.body_md || "",
          cover_url:       res.data.cover_url || "",
          sector_tags:     res.data.sector_tags || [],
          author:          res.data.author || "Connexis Insights",
          seo_title:       res.data.seo_title || "",
          seo_description: res.data.seo_description || "",
          status:          res.data.status || "draft",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load");
        nav("/admin/content");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, nav]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (form.sector_tags.includes(t)) { setTagInput(""); return; }
    setForm((f) => ({ ...f, sector_tags: [...f.sector_tags, t] }));
    setTagInput("");
  };
  const removeTag = (t) => setForm((f) => ({ ...f, sector_tags: f.sector_tags.filter((x) => x !== t) }));

  const wordCount = useMemo(() => (form.body_md.match(/\w+/g) || []).length, [form.body_md]);
  const readingMins = Math.max(1, Math.round(wordCount / 220));

  const save = async (publish = false) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        status: publish ? "published" : form.status,
      };
      let res;
      if (isNew) {
        res = await api.post("/admin/content/articles", payload);
        toast.success(publish ? "Published" : "Saved as draft");
        nav(`/admin/content/${res.data.id}`, { replace: true });
      } else {
        res = await api.put(`/admin/content/articles/${id}`, payload);
        setForm((f) => ({ ...f, status: res.data.status }));
        toast.success(publish ? "Published" : "Saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="size-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-content-editor">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/content" className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.14em] uppercase text-slate-500 hover:text-slate-900 font-semibold" data-testid="editor-back">
            <ArrowLeft className="size-3" /> All articles
          </Link>
          <h1 className="mt-2 text-[26px] tracking-[-0.02em] font-semibold text-slate-900">
            {isNew ? "New article" : "Edit article"}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-[12px] text-slate-500">
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-medium ${form.status === "published" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-50 text-slate-500 border border-slate-200"}`}>
              {form.status}
            </span>
            <span>·</span>
            <span className="font-mono tabular-nums">{wordCount} words · {readingMins} min read</span>
            {!isNew && form.slug && form.status === "published" && (
              <>
                <span>·</span>
                <Link to={`/insights/${form.slug}`} className="inline-flex items-center gap-1 text-[#0055FF] hover:underline">
                  View live <ExternalLink className="size-3" />
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview((p) => !p)}
            className="rounded-full border-slate-300"
            data-testid="editor-toggle-preview"
          >
            {showPreview ? <><EyeOff className="size-3.5 mr-1" /> Hide preview</> : <><Eye className="size-3.5 mr-1" /> Show preview</>}
          </Button>
          <Button
            variant="outline"
            onClick={() => save(false)}
            disabled={saving}
            className="rounded-full border-slate-300"
            data-testid="editor-save-draft"
          >
            {saving ? <Loader2 className="size-3.5 animate-spin" /> : <><Save className="size-3.5 mr-1" /> Save draft</>}
          </Button>
          <Button
            onClick={() => save(true)}
            disabled={saving}
            className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]"
            data-testid="editor-publish"
          >
            {saving ? <Loader2 className="size-3.5 animate-spin" /> : (form.status === "published" ? "Update" : "Publish")}
          </Button>
        </div>
      </div>

      {/* Metadata */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="editor-meta">
        <div className="grid sm:grid-cols-[2fr_1fr] gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Title *</Label>
            <Input value={form.title} onChange={setField("title")} placeholder="Article title" className="mt-1.5 h-11" data-testid="editor-title" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Slug</Label>
            <Input value={form.slug} onChange={setField("slug")} placeholder="auto from title" className="mt-1.5 h-11 font-mono text-[13px]" data-testid="editor-slug" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Excerpt</Label>
          <Textarea value={form.excerpt} onChange={setField("excerpt")} placeholder="One-sentence summary — shows on the /insights list and in social previews." className="mt-1.5 min-h-[70px] resize-none" data-testid="editor-excerpt" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Author</Label>
            <Input value={form.author} onChange={setField("author")} className="mt-1.5 h-11" data-testid="editor-author" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Cover URL (optional)</Label>
            <Input value={form.cover_url} onChange={setField("cover_url")} placeholder="https://…" className="mt-1.5 h-11 font-mono text-[13px]" data-testid="editor-cover" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Sector tags</Label>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            {form.sector_tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF]/10 text-[#0055FF] px-2.5 py-1 text-[12px] font-medium">
                {t}
                <button type="button" onClick={() => removeTag(t)} className="hover:text-[#0044CC]" data-testid={`editor-tag-remove-${t}`}>
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <div className="inline-flex items-center gap-1">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Add tag + Enter"
                className="h-8 w-40 text-[13px]"
                data-testid="editor-tag-input"
              />
              <button
                type="button"
                onClick={addTag}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                data-testid="editor-tag-add"
              >
                <Plus className="size-3.5" />
              </button>
            </div>
          </div>
        </div>
        <details className="rounded-lg border border-slate-200 bg-slate-50/50 group">
          <summary className="cursor-pointer px-4 py-2.5 text-[12px] font-semibold text-slate-600 uppercase tracking-[0.12em] hover:text-slate-900">
            SEO tags
          </summary>
          <div className="px-4 pb-4 pt-1 space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-600 font-semibold">SEO title</Label>
              <Input value={form.seo_title} onChange={setField("seo_title")} placeholder="defaults to article title" className="mt-1.5 h-10 text-[13px]" data-testid="editor-seo-title" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.14em] text-slate-600 font-semibold">SEO description</Label>
              <Textarea value={form.seo_description} onChange={setField("seo_description")} placeholder="defaults to excerpt (max ~150 chars)" className="mt-1.5 min-h-[60px] resize-none text-[13px]" data-testid="editor-seo-description" />
            </div>
          </div>
        </details>
      </div>

      {/* Attributable share URLs — only meaningful once the article has a slug */}
      {form.slug && !isNew && (
        <ShareUrlBuilder slug={form.slug} />
      )}

      {/* Split editor */}
      <div className={`grid gap-4 ${showPreview ? "lg:grid-cols-2" : ""}`}>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 flex items-center justify-between">
            <span>Markdown</span>
            <span className="font-mono text-[10.5px] text-slate-400 tabular-nums">GFM · tables + lists</span>
          </div>
          <Textarea
            value={form.body_md}
            onChange={setField("body_md")}
            placeholder="Write your article in markdown…"
            className="min-h-[560px] rounded-none border-0 focus-visible:ring-0 font-mono text-[13px] leading-[1.6] resize-none"
            data-testid="editor-body"
          />
        </div>

        {showPreview && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Live preview
            </div>
            <div className="p-6 max-h-[600px] overflow-y-auto" data-testid="editor-preview">
              {form.body_md ? (
                <div className="prose-connexis">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={previewComponents}>
                    {form.body_md}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="text-center py-16 text-slate-400 text-[13.5px]">
                  Start writing on the left to see the preview here.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Lightweight preview components — mirror the /insights/:slug renderer.
const previewComponents = {
  h1: ({ children, ...p }) => <h1 className="text-2xl font-semibold text-slate-900 tracking-[-0.02em]" {...p}>{children}</h1>,
  h2: ({ children, ...p }) => <h2 className="mt-8 mb-3 text-xl font-semibold text-slate-900 tracking-[-0.02em]" {...p}>{children}</h2>,
  h3: ({ children, ...p }) => <h3 className="mt-6 mb-2 text-lg font-semibold text-slate-900" {...p}>{children}</h3>,
  p:  ({ children, ...p }) => <p className="my-3 text-[14px] text-slate-700 leading-[1.7]" {...p}>{children}</p>,
  a:  ({ children, ...p }) => <a className="text-[#0055FF] hover:underline" {...p}>{children}</a>,
  ul: ({ children, ...p }) => <ul className="my-3 pl-5 space-y-1.5 list-disc marker:text-slate-400" {...p}>{children}</ul>,
  ol: ({ children, ...p }) => <ol className="my-3 pl-5 space-y-1.5 list-decimal marker:text-slate-500" {...p}>{children}</ol>,
  li: ({ children, ...p }) => <li className="text-[14px] text-slate-700 leading-[1.6]" {...p}>{children}</li>,
  strong: ({ children, ...p }) => <strong className="font-semibold text-slate-900" {...p}>{children}</strong>,
  em: ({ children, ...p }) => <em className="italic" {...p}>{children}</em>,
  code: ({ inline, children, ...p }) => inline
    ? <code className="rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[12px] font-mono text-slate-800" {...p}>{children}</code>
    : <code className="block rounded-lg bg-slate-900 text-slate-100 p-3 text-[12px] font-mono leading-relaxed overflow-x-auto" {...p}>{children}</code>,
  blockquote: ({ children, ...p }) => <blockquote className="my-4 pl-4 border-l-2 border-[#0055FF]/40 italic text-slate-600" {...p}>{children}</blockquote>,
  hr: () => <hr className="my-6 border-t border-slate-200" />,
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-[12.5px] border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
  th: ({ children }) => <th className="text-left px-3 py-1.5 font-semibold text-slate-700 text-[11px] uppercase tracking-widest border-b border-slate-200">{children}</th>,
  td: ({ children }) => <td className="px-3 py-1.5 text-slate-700 border-b border-slate-100 last:border-b-0">{children}</td>,
};

/**
 * ShareUrlBuilder — generates copy-ready UTM-tagged share links for the
 * current article. Every URL sets utm_source + utm_medium + utm_campaign
 * (campaign = article slug) so the /admin/content Traffic Sources panel
 * gets clean channel attribution from day one.
 */
const PRESETS = [
  { key: "twitter",    label: "Twitter · X", color: "#1da1f2", utm: { utm_source: "twitter",    utm_medium: "social"   } },
  { key: "linkedin",   label: "LinkedIn",    color: "#0077b5", utm: { utm_source: "linkedin",   utm_medium: "social"   } },
  { key: "newsletter", label: "Newsletter",  color: "#f59e0b", utm: { utm_source: "newsletter", utm_medium: "email"    } },
  { key: "partner",    label: "Partner",     color: "#a855f7", utm: { utm_source: "partner",    utm_medium: "referral" } },
];

function ShareUrlBuilder({ slug }) {
  // Always point share/QR links at the canonical production domain,
  // regardless of whether the admin is currently editing on preview or
  // on production. Otherwise a QR generated on preview would send
  // real-world scanners to the preview URL instead of connex.is.
  const origin = (process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "");
  const [customSource, setCustomSource] = useState("");
  const [copiedKey, setCopiedKey] = useState(null);

  const buildUrl = (utm) => {
    const params = new URLSearchParams();
    Object.entries(utm).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set("utm_campaign", slug);
    return `${origin}/insights/${slug}?${params.toString()}`;
  };

  const copy = async (key, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const customSanitised = customSource.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  const customUrl = customSanitised ? buildUrl({ utm_source: customSanitised, utm_medium: "custom" }) : "";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="share-url-builder">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.22em] uppercase text-slate-600">
          <Link2 className="size-3 text-[#0055FF]" />
          Attributable share URLs
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono text-slate-400">
          <span>
            <span className="text-slate-500">domain</span> = <span className="text-slate-700">{origin.replace(/^https?:\/\//, "")}</span>
          </span>
          <span className="text-slate-300">·</span>
          <span>
            <span className="text-slate-500">utm_campaign</span> = <span className="text-slate-700">{slug}</span>
          </span>
        </div>
      </div>
      <p className="mt-1.5 text-[13px] text-slate-500 leading-relaxed">
        Copy-ready links tagged so every share is attributable in the traffic-sources panel. Always point at the live site — safe to copy from any environment.
      </p>

      <div className="mt-5 space-y-2">
        {PRESETS.map((p) => {
          const url = buildUrl(p.utm);
          const copied = copiedKey === p.key;
          return (
            <div
              key={p.key}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-white transition-colors p-2.5"
              data-testid={`share-row-${p.key}`}
            >
              <span className="inline-flex items-center gap-2 w-32 shrink-0 text-[13px] font-semibold text-slate-800">
                <span className="size-1.5 rounded-full" style={{ background: p.color }} />
                {p.label}
              </span>
              <input
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 bg-transparent text-[12px] font-mono text-slate-600 focus:outline-none truncate"
                data-testid={`share-url-${p.key}`}
              />
              <QrButton url={url} label={p.label} slug={slug} testId={`share-qr-${p.key}`} />
              <button
                type="button"
                onClick={() => copy(p.key, url)}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-slate-400 bg-white px-3 h-8 text-[11.5px] font-semibold text-slate-700 shrink-0"
                data-testid={`share-copy-${p.key}`}
              >
                {copied ? <><Check className="size-3 text-emerald-500" /> Copied</> : <><Copy className="size-3" /> Copy</>}
              </button>
            </div>
          );
        })}

        {/* Custom source row */}
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white p-2.5" data-testid="share-row-custom">
          <div className="inline-flex items-center gap-2 w-32 shrink-0">
            <span className="size-1.5 rounded-full bg-slate-400 ml-0.5" />
            <Input
              value={customSource}
              onChange={(e) => setCustomSource(e.target.value)}
              placeholder="custom source…"
              className="h-8 text-[12px] px-2 border-slate-200 w-full"
              data-testid="share-custom-source"
            />
          </div>
          <input
            readOnly
            value={customUrl}
            onFocus={(e) => e.target.select()}
            placeholder={customSource ? "" : "Type a source to generate a URL"}
            className="flex-1 min-w-0 bg-transparent text-[12px] font-mono text-slate-600 focus:outline-none placeholder:text-slate-400 truncate"
            data-testid="share-url-custom"
          />
          {customUrl && (
            <QrButton url={customUrl} label={customSanitised} slug={slug} testId="share-qr-custom" />
          )}
          <button
            type="button"
            disabled={!customUrl}
            onClick={() => copy("custom", customUrl)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 h-8 text-[11.5px] font-semibold shrink-0 ${customUrl ? "border-slate-300 hover:border-slate-400 bg-white text-slate-700" : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"}`}
            data-testid="share-copy-custom"
          >
            {copiedKey === "custom" ? <><Check className="size-3 text-emerald-500" /> Copied</> : <><Copy className="size-3" /> Copy</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * QrButton — popover trigger showing a scannable QR code for a UTM-tagged
 * URL, with a one-click PNG download at 3× resolution so it prints crisply
 * on printed collateral / conference booths / partner decks.
 */
function QrButton({ url, label, slug, testId }) {
  const svgRef = useRef(null);

  const downloadPng = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new window.Image();
    img.onload = () => {
      const scale = 3; // print-friendly resolution
      const canvas = document.createElement("canvas");
      canvas.width  = 240 * scale;
      canvas.height = 240 * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement("a");
        const clean = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-|-$/g, "");
        a.href = URL.createObjectURL(blob);
        a.download = `connexis-${clean(slug)}-${clean(label) || "qr"}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      }, "image/png");
      URL.revokeObjectURL(svgUrl);
    };
    img.src = svgUrl;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full border border-slate-300 hover:border-slate-400 bg-white size-8 text-slate-600 hover:text-slate-900 shrink-0"
          title="Show QR code"
          data-testid={testId}
        >
          <QrCode className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" side="left" align="start">
        <div className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-slate-500">
          {label} · QR
        </div>
        <div className="mt-2 text-[13px] font-semibold text-slate-900 truncate" title={slug}>
          /{slug}
        </div>
        <div className="mt-3 grid place-items-center rounded-xl bg-white border border-slate-200 p-3">
          <QRCodeSVG
            ref={svgRef}
            value={url}
            size={200}
            level="M"
            marginSize={2}
            bgColor="#ffffff"
            fgColor="#0f172a"
          />
        </div>
        <button
          type="button"
          onClick={downloadPng}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white px-3 h-9 text-[12.5px] font-semibold shadow-[0_4px_16px_rgba(0,85,255,0.3)]"
          data-testid={`${testId}-download`}
        >
          <Download className="size-3.5" /> Download PNG
        </button>
        <p className="mt-2 text-[11px] text-slate-400 leading-snug text-center">
          Every scan lands with the UTM tag intact — auto-attributed in the sources panel.
        </p>
      </PopoverContent>
    </Popover>
  );
}
